interface MenuAnchor {
  /** The cell element the menu opens from. */
  anchor: { current: Element | null };
  /** Whether this cell's menu is open. react-select runs the handler for every scroll on the page, open or not. */
  isOpen: () => boolean;
  /** Re-renders the select, which places its portalled menu again from the cell's current position. */
  onFollow: () => void;
}

interface VisibleBounds {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * The part of the screen a scrolled element shows: its own box, or the viewport for the page.
 * @param scrolled - the element or document that scrolled.
 * @returns its visible bounds in viewport coordinates.
 */
function visibleBoundsOf(scrolled: Node): VisibleBounds {
  if (scrolled instanceof Element) return scrolled.getBoundingClientRect();
  return { top: 0, left: 0, bottom: window.innerHeight, right: window.innerWidth };
}

/**
 * Whether two boxes share any area.
 * @param box - the first box.
 * @param bounds - the second box.
 * @returns true when they overlap.
 */
function overlaps(box: VisibleBounds, bounds: VisibleBounds): boolean {
  return box.top < bounds.bottom && box.bottom > bounds.top && box.left < bounds.right && box.right > bounds.left;
}

/**
 * A react-select `closeMenuOnScroll` handler for a menu portalled to `<body>` with a fixed position,
 * which react-select places from its cell once and never follows. When something that moves the
 * cell scrolls (the grid body, a scrolling ancestor, the page), the menu follows the cell while the
 * cell is still in view, and closes once the cell has scrolled out of view. Other scrolls, such as
 * the menu's own option list, and every scroll while the menu is closed, are left alone.
 * @param menuAnchor - the cell, whether its menu is open, and how to re-place the menu.
 * @returns the handler react-select calls with each scroll event; true closes the menu.
 */
const closeMenuOnAnchorScroll =
  ({ anchor, isOpen, onFollow }: MenuAnchor) =>
  (event: Event): boolean => {
    const cell = anchor.current;
    const scrolled = event.target;
    if (!isOpen() || !cell || !(scrolled instanceof Node) || !scrolled.contains(cell)) return false;
    if (!overlaps(cell.getBoundingClientRect(), visibleBoundsOf(scrolled))) return true;
    onFollow();
    return false;
  };

export default closeMenuOnAnchorScroll;
